import type { UndoableState } from '../../vendor/src/features/remotion-editor/editor/state/types';

/** Ephemeral bundle paths, validated against the original portable HTTPS source by asset ID. */
export type PreparedNativeAssets = Readonly<Record<string, Readonly<{
  sourceUrl: string;
  staticPath: string;
}>>>;

const mediaExtensions: Readonly<Record<string, readonly string[]>> = {
  video: ['mp4', 'webm', 'mov'],
  audio: ['mp3', 'm4a', 'aac', 'wav', 'flac', 'mp4', 'webm', 'mov'],
  image: ['png', 'jpg', 'gif', 'webp', 'avif', 'svg'],
  gif: ['gif'],
  webp: ['webp'],
  pdf: ['pdf'],
};
const nativeMediaPath = /^native-media\/[0-9a-f]{64}\.([a-z0-9]+)$/;
const preparedNativeAssetPaths = new WeakMap<object, {path:string;sourceUrl:string;id:string;type:string}>();

/** Only the exact render-copy asset receives a transport path; saved/cloned assets do not. */
export function getPreparedNativeAssetPath(asset: object): string | undefined {
  const binding=preparedNativeAssetPaths.get(asset);
  if(!binding)return undefined;
  const current=asset as Record<string,unknown>;
  if(current.remoteUrl!==binding.sourceUrl||current.id!==binding.id||current.type!==binding.type)
    throw new Error('준비된 미디어의 원본 연결이 변경됐습니다.');
  return binding.path;
}

function isHttpsSource(value: unknown): value is string {
  if (typeof value !== 'string' || value !== value.trim()) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !!url.hostname && !url.username && !url.password;
  } catch { return false; }
}

/** Called only after readNativeDocumentFile accepts the untouched saved file. */
export function applyPreparedNativeAssets(
  document: Pick<UndoableState, 'assets'>,
  prepared: PreparedNativeAssets | undefined,
  browserVideoIds: readonly string[] = [],
): UndoableState['assets'] {
  const assets = {...document.assets};
  if (prepared === undefined) return assets;
  if (!prepared || typeof prepared !== 'object' || Array.isArray(prepared))
    throw new Error('준비된 미디어 매핑을 확인해 주세요.');
  const browserIds = new Set(browserVideoIds);
  for (const [id, entry] of Object.entries(prepared)) {
    const asset = Object.hasOwn(assets, id) ? assets[id] : undefined;
    const sourceUrl = entry?.sourceUrl;
    const staticPath = entry?.staticPath;
    if (!asset || !Object.hasOwn(mediaExtensions, asset.type) || browserIds.has(id) ||
        !entry || typeof entry !== 'object' || Array.isArray(entry) ||
        Object.keys(entry).length !== 2 || !Object.hasOwn(entry, 'sourceUrl') || !Object.hasOwn(entry, 'staticPath') ||
        !isHttpsSource(asset.remoteUrl) || sourceUrl !== asset.remoteUrl ||
        typeof staticPath !== 'string')
      throw new Error('준비된 미디어가 편집 원본과 일치하지 않습니다.');
    const match = nativeMediaPath.exec(staticPath);
    if (!match || match[0] !== staticPath || !mediaExtensions[asset.type]!.includes(match[1]!))
      throw new Error('준비된 미디어 경로가 올바르지 않습니다.');
    const renderAsset = {...asset};
    preparedNativeAssetPaths.set(renderAsset, {path:staticPath,sourceUrl,id:asset.id,type:asset.type});
    assets[id] = renderAsset;
  }
  return assets;
}
